import type { ComponentType } from 'react';
import type { ShareState } from '../utils/shareUtils';
import {
  MORTGAGE_CALCULATOR_ONTARIO_GUIDE_STATE,
  MORTGAGE_CALCULATOR_BC_GUIDE_STATE,
  MORTGAGE_CALCULATOR_ALBERTA_GUIDE_STATE,
  ONTARIO_GUIDE_TAB_IS_TORONTO,
} from '../utils/articleScenarios';
import { OntarioCalculatorContext } from '../components/province-calculators/content/OntarioCalculatorContext';
import { BcCalculatorContext } from '../components/province-calculators/content/BcCalculatorContext';
import { AlbertaCalculatorContext } from '../components/province-calculators/content/AlbertaCalculatorContext';

export type ProvinceCalculatorId = 'ontario' | 'bc' | 'alberta';

export interface ProvinceCalculatorPageConfig {
  id: ProvinceCalculatorId;
  path: string;
  h1: string;
  title: string;
  description: string;
  shareState: ShareState;
  contextNavLabel: string;
  ContextContent: ComponentType;
  /** Ontario only: map tab id to whether Toronto municipal LTT applies. */
  tabIsToronto?: Record<string, boolean>;
}

export const ONTARIO_MORTGAGE_CALCULATOR_PATH = '/calculators/mortgage-calculator-ontario';
export const BC_MORTGAGE_CALCULATOR_PATH = '/calculators/mortgage-calculator-bc';
export const ALBERTA_MORTGAGE_CALCULATOR_PATH = '/calculators/mortgage-calculator-alberta';

export const PROVINCE_CALCULATOR_PAGES: ProvinceCalculatorPageConfig[] = [
  {
    id: 'ontario',
    path: ONTARIO_MORTGAGE_CALCULATOR_PATH,
    h1: 'Ontario mortgage calculator',
    title: 'Ontario Mortgage Calculator | Land Transfer Tax, CMHC PST and GTA Scenarios',
    description:
      'Compare Ontario mortgage scenarios with Toronto dual land transfer tax, CMHC PST at 8%, and stepped minimum down payment rules. Pre-loaded GTA and Ottawa tabs with closing cost breakdown.',
    shareState: MORTGAGE_CALCULATOR_ONTARIO_GUIDE_STATE,
    contextNavLabel: 'Ontario rules and closing costs',
    ContextContent: OntarioCalculatorContext,
    tabIsToronto: ONTARIO_GUIDE_TAB_IS_TORONTO,
  },
  {
    id: 'bc',
    path: BC_MORTGAGE_CALCULATOR_PATH,
    h1: 'BC mortgage calculator',
    title: 'BC Mortgage Calculator | Property Transfer Tax, CMHC PST and Vancouver Scenarios',
    description:
      'Compare BC mortgage scenarios with Property Transfer Tax tiers, first-time buyer exemptions, and 7% CMHC PST. Pre-loaded Vancouver and Victoria tabs with closing cost breakdown.',
    shareState: MORTGAGE_CALCULATOR_BC_GUIDE_STATE,
    contextNavLabel: 'BC rules and closing costs',
    ContextContent: BcCalculatorContext,
  },
  {
    id: 'alberta',
    path: ALBERTA_MORTGAGE_CALCULATOR_PATH,
    h1: 'Alberta mortgage calculator',
    title: 'Alberta Mortgage Calculator | No Land Transfer Tax and Calgary Scenarios',
    description:
      'Compare Alberta mortgage scenarios with no land transfer tax, land title registration fees, and zero CMHC PST. Pre-loaded Calgary and Edmonton tabs with Ontario savings comparison.',
    shareState: MORTGAGE_CALCULATOR_ALBERTA_GUIDE_STATE,
    contextNavLabel: 'Alberta rules and closing costs',
    ContextContent: AlbertaCalculatorContext,
  },
];

export function getProvinceCalculatorByPath(path: string): ProvinceCalculatorPageConfig | undefined {
  return PROVINCE_CALCULATOR_PAGES.find((p) => p.path === path);
}

export function getProvinceCalculatorById(id: ProvinceCalculatorId): ProvinceCalculatorPageConfig {
  const page = PROVINCE_CALCULATOR_PAGES.find((p) => p.id === id);
  if (!page) throw new Error(`Unknown province calculator: ${id}`);
  return page;
}
